import React, { useState } from 'react';
import { Navigation } from '../components/Navigation';
import { CreditCardIcon, ReceiptIcon, DollarSignIcon, ShieldIcon, FileTextIcon, TrendingUpIcon, AlertCircleIcon, CheckCircleIcon, ClockIcon, DownloadIcon, RefreshCwIcon, SearchIcon, FilterIcon, PlusIcon } from 'lucide-react';
export function BillingPayments() {
  const [activeTab, setActiveTab] = useState<'overview' | 'billing' | 'insurance' | 'payments' | 'invoices'>('overview');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState<string | null>(null);
  const [selectedPaymentMode, setSelectedPaymentMode] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // Overview Statistics
  const overviewStats = {
    totalRevenue: 1245000,
    pendingPayments: 145000,
    completedToday: 89,
    insuranceClaims: 23
  };
  // Pending Bills
  const pendingBills = [{
    billNumber: 'INV-2024-0156',
    patientName: 'John Smith',
    date: '2024-01-15',
    items: [{
      category: 'Consultation',
      description: 'Dr. Sarah Johnson - Cardiology',
      quantity: 1,
      rate: 1500,
      amount: 1500,
      tax: 270
    }, {
      category: 'Lab',
      description: 'Complete Blood Count',
      quantity: 1,
      rate: 800,
      amount: 800,
      tax: 144
    }, {
      category: 'Imaging',
      description: 'Chest X-Ray',
      quantity: 1,
      rate: 1200,
      amount: 1200,
      tax: 216
    }],
    subtotal: 3500,
    discount: 350,
    taxTotal: 630,
    total: 3780,
    status: 'Pending'
  }, {
    billNumber: 'INV-2024-0157',
    patientName: 'Emily Davis',
    date: '2024-01-15',
    items: [{
      category: 'Consultation',
      description: 'Dr. Michael Chen - Neurology',
      quantity: 1,
      rate: 2000,
      amount: 2000,
      tax: 360
    }, {
      category: 'Drugs',
      description: 'Medication Package',
      quantity: 1,
      rate: 850,
      amount: 850,
      tax: 153
    }],
    subtotal: 2850,
    discount: 0,
    taxTotal: 513,
    total: 3363,
    status: 'Pending'
  }];
  // Payment History
  const paymentHistory = [{
    id: 1,
    billNumber: 'INV-2024-0155',
    patientName: 'Sarah Wilson',
    date: '2024-01-15',
    amount: 5724,
    paymentMode: 'UPI',
    status: 'Completed'
  }, {
    id: 2,
    billNumber: 'INV-2024-0154',
    patientName: 'Michael Brown',
    date: '2024-01-14',
    amount: 3250,
    paymentMode: 'Card',
    status: 'Completed'
  }, {
    id: 3,
    billNumber: 'INV-2024-0153',
    patientName: 'Jessica Lee',
    date: '2024-01-14',
    amount: 4800,
    paymentMode: 'Bank Transfer',
    status: 'Completed'
  }, {
    id: 4,
    billNumber: 'INV-2024-0152',
    patientName: 'David Miller',
    date: '2024-01-13',
    amount: 2100,
    paymentMode: 'Card',
    status: 'Refunded'
  }];
  // Insurance Claims
  const insuranceClaims = [{
    id: 1,
    claimNumber: 'CLM-2024-015',
    patientName: 'Robert Johnson',
    billNumber: 'INV-2024-0145',
    date: '2024-01-12',
    amount: 8500,
    claimedAmount: 6800,
    status: 'Approved',
    provider: 'Blue Cross Blue Shield',
    approvalDate: '2024-01-14'
  }, {
    id: 2,
    claimNumber: 'CLM-2024-014',
    patientName: 'Linda Martinez',
    billNumber: 'INV-2024-0138',
    date: '2024-01-10',
    amount: 5200,
    claimedAmount: 4160,
    status: 'Processing',
    provider: 'United Healthcare',
    approvalDate: '-'
  }, {
    id: 3,
    claimNumber: 'CLM-2024-013',
    patientName: 'James Anderson',
    billNumber: 'INV-2024-0132',
    date: '2024-01-08',
    amount: 3800,
    claimedAmount: 3040,
    status: 'Pending',
    provider: 'Aetna',
    approvalDate: '-'
  }];
  // Payment Modes
  const paymentModes = [{
    id: 'card',
    name: 'Credit/Debit Card',
    icon: CreditCardIcon
  }, {
    id: 'upi',
    name: 'UPI',
    icon: DollarSignIcon
  }, {
    id: 'wallet',
    name: 'Digital Wallet',
    icon: DollarSignIcon
  }, {
    id: 'pos',
    name: 'POS Terminal',
    icon: CreditCardIcon
  }, {
    id: 'bank',
    name: 'Bank Transfer',
    icon: DollarSignIcon
  }, {
    id: 'invoice',
    name: 'Generate Invoice',
    icon: FileTextIcon
  }];
  // Refunds and Credit Notes
  const refundsCreditNotes = [{
    id: 1,
    type: 'Refund',
    referenceNumber: 'REF-2024-008',
    patientName: 'David Miller',
    billNumber: 'INV-2024-0152',
    date: '2024-01-14',
    amount: 2100,
    status: 'Completed',
    reason: 'Service cancellation'
  }, {
    id: 2,
    type: 'Credit Note',
    referenceNumber: 'CN-2024-005',
    patientName: 'Patricia Garcia',
    billNumber: 'INV-2024-0148',
    date: '2024-01-12',
    amount: 500,
    status: 'Applied',
    reason: 'Billing error adjustment'
  }];
  // Deferred Payments
  const deferredPayments = [{
    id: 1,
    billNumber: 'INV-2024-0140',
    patientName: 'Thomas White',
    originalAmount: 12500,
    paidAmount: 5000,
    remainingAmount: 7500,
    dueDate: '2024-02-15',
    status: 'Active'
  }, {
    id: 2,
    billNumber: 'INV-2024-0135',
    patientName: 'Nancy Taylor',
    originalAmount: 8500,
    paidAmount: 4000,
    remainingAmount: 4500,
    dueDate: '2024-02-10',
    status: 'Active'
  }];
  const handleProcessPayment = () => {
    if (!selectedPaymentMode) {
      alert('Please select a payment mode');
      return;
    }
    const bill = pendingBills.find(b => b.billNumber === selectedBill);
    if (bill) {
      alert(`Processing payment of ₹${bill.total} via ${paymentModes.find(p => p.id === selectedPaymentMode)?.name}`);
    }
    setShowPaymentModal(false);
    setSelectedBill(null);
    setSelectedPaymentMode('');
  };
  const handleInitiatePayment = (billNumber: string) => {
    setSelectedBill(billNumber);
    setShowPaymentModal(true);
  };
  return <div className="min-h-screen bg-gray-50 w-full" data-id="element-1376">
      <Navigation data-id="element-1377" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1378">
        {/* Header */}
        <div className="flex items-center justify-between mb-6" data-id="element-1379">
          <div className="flex items-center space-x-3" data-id="element-1380">
            <CreditCardIcon className="w-8 h-8 text-yellow-600" data-id="element-1381" />
            <div data-id="element-1382">
              <h1 className="text-3xl font-bold text-gray-900" data-id="element-1383">
                Billing & Payments
              </h1>
              <p className="text-gray-600" data-id="element-1384">
                Comprehensive payment management and financial operations
              </p>
            </div>
          </div>
          <button className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700" data-id="element-1385">
            <PlusIcon className="w-5 h-5" data-id="element-1386" />
            <span data-id="element-1387">New Bill</span>
          </button>
        </div>
        {/* Search and Filter */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6" data-id="element-1388">
          <div className="flex flex-col md:flex-row gap-4" data-id="element-1389">
            <div className="flex-1 relative" data-id="element-1390">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1391" />
              <input type="text" placeholder="Search by bill number, patient name, or transaction ID..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent" data-id="element-1392" />
            </div>
            <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-1393">
              <FilterIcon className="w-5 h-5" data-id="element-1394" />
              <span data-id="element-1395">Filters</span>
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-4 mb-6" data-id="element-1396">
          <button onClick={() => setActiveTab('overview')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'overview' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1397">
            <TrendingUpIcon className="w-4 h-4 inline mr-2" data-id="element-1398" />
            Overview
          </button>
          <button onClick={() => setActiveTab('billing')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'billing' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1399">
            <ReceiptIcon className="w-4 h-4 inline mr-2" data-id="element-1400" />
            Billing & Checkout
          </button>
          <button onClick={() => setActiveTab('insurance')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'insurance' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1401">
            <ShieldIcon className="w-4 h-4 inline mr-2" data-id="element-1402" />
            Insurance Claims
          </button>
          <button onClick={() => setActiveTab('payments')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'payments' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1403">
            <DollarSignIcon className="w-4 h-4 inline mr-2" data-id="element-1404" />
            Payments & Refunds
          </button>
          <button onClick={() => setActiveTab('invoices')} className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'invoices' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1405">
            <FileTextIcon className="w-4 h-4 inline mr-2" data-id="element-1406" />
            Invoices & Tax
          </button>
        </div>
        {/* Overview Tab */}
        {activeTab === 'overview' && <>
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-1407">
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-1408">
                <div className="flex items-center space-x-2 mb-2" data-id="element-1409">
                  <DollarSignIcon className="w-5 h-5 text-green-600" data-id="element-1410" />
                  <span className="text-sm text-gray-600" data-id="element-1411">Total Revenue</span>
                </div>
                <p className="text-2xl font-bold text-gray-900" data-id="element-1412">
                  ₹{overviewStats.totalRevenue.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-1413">This Month</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-1414">
                <div className="flex items-center space-x-2 mb-2" data-id="element-1415">
                  <ClockIcon className="w-5 h-5 text-orange-600" data-id="element-1416" />
                  <span className="text-sm text-gray-600" data-id="element-1417">
                    Pending Payments
                  </span>
                </div>
                <p className="text-2xl font-bold text-gray-900" data-id="element-1418">
                  ₹{overviewStats.pendingPayments.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-1419">
                  {pendingBills.length} Bills
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-1420">
                <div className="flex items-center space-x-2 mb-2" data-id="element-1421">
                  <CheckCircleIcon className="w-5 h-5 text-blue-600" data-id="element-1422" />
                  <span className="text-sm text-gray-600" data-id="element-1423">Completed Today</span>
                </div>
                <p className="text-2xl font-bold text-gray-900" data-id="element-1424">
                  {overviewStats.completedToday}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-1425">Transactions</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-1426">
                <div className="flex items-center space-x-2 mb-2" data-id="element-1427">
                  <ShieldIcon className="w-5 h-5 text-purple-600" data-id="element-1428" />
                  <span className="text-sm text-gray-600" data-id="element-1429">
                    Insurance Claims
                  </span>
                </div>
                <p className="text-2xl font-bold text-gray-900" data-id="element-1430">
                  {overviewStats.insuranceClaims}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-1431">Active Claims</p>
              </div>
            </div>
            {/* Pending Bills Overview */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6" data-id="element-1432">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1433">
                Pending Bills
              </h3>
              <div className="space-y-4" data-id="element-1434">
                {pendingBills.map(bill => <div key={bill.billNumber} className="border border-gray-200 rounded-lg p-4" data-id="element-1435">
                    <div className="flex items-start justify-between mb-3" data-id="element-1436">
                      <div data-id="element-1437">
                        <h4 className="font-semibold text-gray-900" data-id="element-1438">
                          {bill.billNumber}
                        </h4>
                        <p className="text-sm text-gray-600" data-id="element-1439">
                          Patient: {bill.patientName}
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-1440">
                          Date: {bill.date}
                        </p>
                      </div>
                      <div className="text-right" data-id="element-1441">
                        <p className="text-2xl font-bold text-gray-900" data-id="element-1442">
                          ₹{bill.total.toLocaleString()}
                        </p>
                        <span className="px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800" data-id="element-1443">
                          {bill.status}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-200" data-id="element-1444">
                      <div className="text-sm text-gray-600" data-id="element-1445">
                        {bill.items.length} items • Subtotal: ₹
                        {bill.subtotal.toLocaleString()}
                      </div>
                      <button onClick={() => handleInitiatePayment(bill.billNumber)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm" data-id="element-1446">
                        Process Payment
                      </button>
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Recent Transactions */}
            <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-1447">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1448">
                Recent Transactions
              </h3>
              <div className="overflow-x-auto" data-id="element-1449">
                <table className="w-full" data-id="element-1450">
                  <thead className="bg-gray-50 border-b border-gray-200" data-id="element-1451">
                    <tr data-id="element-1452">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1453">
                        Bill Number
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1454">
                        Patient
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1455">
                        Date
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-1456">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1457">
                        Payment Mode
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1458">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-1459">
                    {paymentHistory.slice(0, 5).map(payment => <tr key={payment.id} className="hover:bg-gray-50" data-id="element-1460">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1461">
                          {payment.billNumber}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1462">
                          {payment.patientName}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1463">
                          {payment.date}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-gray-900" data-id="element-1464">
                          ₹{payment.amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1465">
                          {payment.paymentMode}
                        </td>
                        <td className="px-4 py-3" data-id="element-1466">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${payment.status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1467">
                            {payment.status}
                          </span>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
          </>}
        {/* Billing & Checkout Tab */}
        {activeTab === 'billing' && <>
            {/* Pending Bills with Itemized Details */}
            {pendingBills.map(bill => <div key={bill.billNumber} className="bg-white border border-gray-200 rounded-lg p-6 mb-6" data-id="element-1468">
                {/* Bill Header */}
                <div className="flex items-start justify-between mb-4" data-id="element-1469">
                  <div data-id="element-1470">
                    <h3 className="text-xl font-bold text-gray-900" data-id="element-1471">
                      {bill.billNumber}
                    </h3>
                    <p className="text-gray-600" data-id="element-1472">Patient: {bill.patientName}</p>
                    <p className="text-sm text-gray-500" data-id="element-1473">Date: {bill.date}</p>
                  </div>
                  <div className="text-right" data-id="element-1474">
                    <p className="text-3xl font-bold text-blue-600" data-id="element-1475">
                      ₹{bill.total.toLocaleString()}
                    </p>
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800" data-id="element-1476">
                      {bill.status}
                    </span>
                  </div>
                </div>
                {/* Itemized Bill */}
                <div className="overflow-x-auto mb-4" data-id="element-1477">
                  <table className="w-full" data-id="element-1478">
                    <thead className="bg-gray-50 border-b border-gray-200" data-id="element-1479">
                      <tr data-id="element-1480">
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1481">
                          Category
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1482">
                          Description
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-1483">
                          Qty
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-1484">
                          Rate
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-1485">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-1486">
                          Tax
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-1487">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200" data-id="element-1488">
                      {bill.items.map((item, index) => <tr key={index} className="hover:bg-gray-50" data-id="element-1489">
                          <td className="px-4 py-3" data-id="element-1490">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${item.category === 'Consultation' ? 'bg-purple-100 text-purple-800' : item.category === 'Lab' ? 'bg-orange-100 text-orange-800' : item.category === 'Drugs' ? 'bg-green-100 text-green-800' : item.category === 'Consumables' ? 'bg-blue-100 text-blue-800' : item.category === 'Imaging' ? 'bg-pink-100 text-pink-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1491">
                              {item.category}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900" data-id="element-1492">
                            {item.description}
                          </td>
                          <td className="px-4 py-3 text-sm text-center text-gray-700" data-id="element-1493">
                            {item.quantity}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-gray-700" data-id="element-1494">
                            ₹{item.rate}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 font-medium" data-id="element-1495">
                            ₹{item.amount}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-gray-600" data-id="element-1496">
                            ₹{item.tax}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 font-semibold" data-id="element-1497">
                            ₹{item.amount + item.tax}
                          </td>
                        </tr>)}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t-2 border-gray-300" data-id="element-1498">
                      <tr data-id="element-1499">
                        <td colSpan={4} className="px-4 py-3 text-right text-sm font-medium text-gray-700" data-id="element-1500">
                          Subtotal:
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900" data-id="element-1501">
                          ₹{bill.subtotal}
                        </td>
                        <td colSpan={2} data-id="element-1502"></td>
                      </tr>
                      {bill.discount > 0 && <tr data-id="element-1503">
                          <td colSpan={4} className="px-4 py-3 text-right text-sm font-medium text-gray-700" data-id="element-1504">
                            Discount:
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-semibold text-green-600" data-id="element-1505">
                            -₹{bill.discount}
                          </td>
                          <td colSpan={2} data-id="element-1506"></td>
                        </tr>}
                      <tr data-id="element-1507">
                        <td colSpan={4} className="px-4 py-3 text-right text-sm font-medium text-gray-700" data-id="element-1508">
                          Tax (GST 18%):
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900" data-id="element-1509">
                          ₹{bill.taxTotal}
                        </td>
                        <td colSpan={2} data-id="element-1510"></td>
                      </tr>
                      <tr className="border-t-2 border-gray-300" data-id="element-1511">
                        <td colSpan={4} className="px-4 py-4 text-right text-base font-bold text-gray-900" data-id="element-1512">
                          Total Amount:
                        </td>
                        <td className="px-4 py-4 text-right text-lg font-bold text-blue-600" data-id="element-1513">
                          ₹{bill.total}
                        </td>
                        <td colSpan={2} data-id="element-1514"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                {/* Action Buttons */}
                <div className="flex justify-end space-x-3" data-id="element-1515">
                  <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" data-id="element-1516">
                    Edit Bill
                  </button>
                  <button onClick={() => handleInitiatePayment(bill.billNumber)} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold" data-id="element-1517">
                    Process Payment
                  </button>
                </div>
              </div>)}
          </>}
        {/* Insurance Claims Tab */}
        {activeTab === 'insurance' && <>
            {/* Claims Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-1518">
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-1519">
                <div className="flex items-center space-x-2 mb-2" data-id="element-1520">
                  <ShieldIcon className="w-5 h-5 text-blue-600" data-id="element-1521" />
                  <span className="text-sm text-gray-600" data-id="element-1522">Total Claims</span>
                </div>
                <p className="text-2xl font-bold text-gray-900" data-id="element-1523">
                  {insuranceClaims.length}
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-1524">
                <div className="flex items-center space-x-2 mb-2" data-id="element-1525">
                  <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-1526" />
                  <span className="text-sm text-gray-600" data-id="element-1527">Approved</span>
                </div>
                <p className="text-2xl font-bold text-gray-900" data-id="element-1528">
                  {insuranceClaims.filter(claim => claim.status === 'Approved').length}
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-1529">
                <div className="flex items-center space-x-2 mb-2" data-id="element-1530">
                  <ClockIcon className="w-5 h-5 text-orange-600" data-id="element-1531" />
                  <span className="text-sm text-gray-600" data-id="element-1532">Processing</span>
                </div>
                <p className="text-2xl font-bold text-gray-900" data-id="element-1533">
                  {insuranceClaims.filter(claim => claim.status === 'Processing').length}
                </p>
              </div>
            </div>
            {/* Initiate New Claim */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6" data-id="element-1534">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1535">
                Initiate New Insurance Claim
              </h3>
              <div className="space-y-4" data-id="element-1536">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1537">
                  <div data-id="element-1538">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1539">
                      Select Bill
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-id="element-1540">
                      <option value="" data-id="element-1541">Choose a bill</option>
                      {pendingBills.map(bill => <option key={bill.billNumber} value={bill.billNumber} data-id="element-1542">
                          {bill.billNumber} - {bill.patientName} - ₹
                          {bill.total.toLocaleString()}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-1543">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1544">
                      Insurance Provider Format
                    </label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent" data-id="element-1545">
                      <option value="bcbs" data-id="element-1546">
                        Blue Cross Blue Shield (Standard)
                      </option>
                      <option value="uhc" data-id="element-1547">United Healthcare (UHC)</option>
                      <option value="aetna" data-id="element-1548">Aetna (Claims Portal)</option>
                      <option value="cigna" data-id="element-1549">Cigna (Electronic)</option>
                      <option value="generic" data-id="element-1550">Generic Format</option>
                    </select>
                  </div>
                </div>
                <button className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold" data-id="element-1551">
                  Submit Insurance Claim
                </button>
              </div>
            </div>
            {/* Claims List */}
            <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-1552">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1553">
                Insurance Claims
              </h3>
              <div className="space-y-4" data-id="element-1554">
                {insuranceClaims.map(claim => <div key={claim.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1555">
                    <div className="flex items-start justify-between mb-3" data-id="element-1556">
                      <div data-id="element-1557">
                        <h4 className="font-semibold text-gray-900" data-id="element-1558">
                          {claim.claimNumber}
                        </h4>
                        <p className="text-sm text-gray-600" data-id="element-1559">
                          Patient: {claim.patientName}
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-1560">
                          Bill: {claim.billNumber}
                        </p>
                        <p className="text-sm text-gray-600" data-id="element-1561">
                          Provider: {claim.provider}
                        </p>
                      </div>
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${claim.status === 'Approved' ? 'bg-green-100 text-green-800' : claim.status === 'Processing' ? 'bg-yellow-100 text-yellow-800' : 'bg-orange-100 text-orange-800'}`} data-id="element-1562">
                        {claim.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm" data-id="element-1563">
                      <div data-id="element-1564">
                        <p className="text-gray-600" data-id="element-1565">Claim Date</p>
                        <p className="font-medium text-gray-900" data-id="element-1566">
                          {claim.date}
                        </p>
                      </div>
                      <div data-id="element-1567">
                        <p className="text-gray-600" data-id="element-1568">Bill Amount</p>
                        <p className="font-medium text-gray-900" data-id="element-1569">
                          ₹{claim.amount.toLocaleString()}
                        </p>
                      </div>
                      <div data-id="element-1570">
                        <p className="text-gray-600" data-id="element-1571">Claimed Amount</p>
                        <p className="font-medium text-green-600" data-id="element-1572">
                          ₹{claim.claimedAmount.toLocaleString()}
                        </p>
                      </div>
                      <div data-id="element-1573">
                        <p className="text-gray-600" data-id="element-1574">Approval Date</p>
                        <p className="font-medium text-gray-900" data-id="element-1575">
                          {claim.approvalDate}
                        </p>
                      </div>
                    </div>
                    {claim.status === 'Approved' && <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-1576">
                        <button className="text-blue-600 hover:text-blue-700 text-sm font-medium" data-id="element-1577">
                          <DownloadIcon className="w-4 h-4 inline mr-1" data-id="element-1578" />
                          Download Approval Letter
                        </button>
                      </div>}
                  </div>)}
              </div>
            </div>
          </>}
        {/* Payments & Refunds Tab */}
        {activeTab === 'payments' && <>
            {/* Payment Modes */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6" data-id="element-1579">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1580">
                Available Payment Methods
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1581">
                {paymentModes.map(mode => {
              const Icon = mode.icon;
              return <div key={mode.id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:shadow-md transition-all cursor-pointer" data-id="element-1582">
                      <div className="flex items-center space-x-3" data-id="element-1583">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-1584">
                          <Icon className="w-6 h-6 text-blue-600" data-id="element-1585" />
                        </div>
                        <div data-id="element-1586">
                          <h4 className="font-medium text-gray-900" data-id="element-1587">
                            {mode.name}
                          </h4>
                          <p className="text-xs text-gray-500" data-id="element-1588">Fast & Secure</p>
                        </div>
                      </div>
                    </div>;
            })}
              </div>
            </div>
            {/* Payment History */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6" data-id="element-1589">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1590">
                Payment History
              </h3>
              <div className="overflow-x-auto" data-id="element-1591">
                <table className="w-full" data-id="element-1592">
                  <thead className="bg-gray-50 border-b border-gray-200" data-id="element-1593">
                    <tr data-id="element-1594">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1595">
                        Bill Number
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1596">
                        Patient
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1597">
                        Date
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-1598">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1599">
                        Payment Mode
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1600">
                        Status
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-1601">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-1602">
                    {paymentHistory.map(payment => <tr key={payment.id} className="hover:bg-gray-50" data-id="element-1603">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1604">
                          {payment.billNumber}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1605">
                          {payment.patientName}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1606">
                          {payment.date}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-medium text-gray-900" data-id="element-1607">
                          ₹{payment.amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1608">
                          {payment.paymentMode}
                        </td>
                        <td className="px-4 py-3" data-id="element-1609">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${payment.status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-1610">
                            {payment.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center" data-id="element-1611">
                          <button className="text-blue-600 hover:text-blue-700 text-sm" data-id="element-1612">
                            <DownloadIcon className="w-4 h-4 inline" data-id="element-1613" />
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Refunds & Credit Notes */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6" data-id="element-1614">
              <div className="flex items-center justify-between mb-4" data-id="element-1615">
                <h3 className="text-lg font-semibold text-gray-900" data-id="element-1616">
                  Refunds & Credit Notes
                </h3>
                <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm" data-id="element-1617">
                  Process Refund
                </button>
              </div>
              <div className="space-y-3" data-id="element-1618">
                {refundsCreditNotes.map(item => <div key={item.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1619">
                    <div className="flex items-start justify-between mb-2" data-id="element-1620">
                      <div className="flex items-center space-x-2" data-id="element-1621">
                        <RefreshCwIcon className={`w-5 h-5 ${item.type === 'Refund' ? 'text-red-600' : 'text-blue-600'}`} data-id="element-1622" />
                        <div data-id="element-1623">
                          <h4 className="font-semibold text-gray-900" data-id="element-1624">
                            {item.referenceNumber}
                          </h4>
                          <p className="text-sm text-gray-600" data-id="element-1625">{item.type}</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${item.status === 'Completed' ? 'bg-green-100 text-green-800' : item.status === 'Applied' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-1626">
                        {item.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm" data-id="element-1627">
                      <div data-id="element-1628">
                        <p className="text-gray-600" data-id="element-1629">Patient</p>
                        <p className="font-medium text-gray-900" data-id="element-1630">
                          {item.patientName}
                        </p>
                      </div>
                      <div data-id="element-1631">
                        <p className="text-gray-600" data-id="element-1632">Bill Number</p>
                        <p className="font-medium text-gray-900" data-id="element-1633">
                          {item.billNumber}
                        </p>
                      </div>
                      <div data-id="element-1634">
                        <p className="text-gray-600" data-id="element-1635">Date</p>
                        <p className="font-medium text-gray-900" data-id="element-1636">{item.date}</p>
                      </div>
                      <div data-id="element-1637">
                        <p className="text-gray-600" data-id="element-1638">Amount</p>
                        <p className="font-medium text-gray-900" data-id="element-1639">
                          ₹{item.amount.toLocaleString()}
                        </p>
                      </div>
                      <div data-id="element-1640">
                        <p className="text-gray-600" data-id="element-1641">Reason</p>
                        <p className="font-medium text-gray-900" data-id="element-1642">
                          {item.reason}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Deferred Payments */}
            <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-1643">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1644">
                Deferred Payment Plans
              </h3>
              <div className="space-y-4" data-id="element-1645">
                {deferredPayments.map(payment => <div key={payment.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1646">
                    <div className="flex items-start justify-between mb-3" data-id="element-1647">
                      <div className="flex items-center space-x-2" data-id="element-1648">
                        <ClockIcon className="w-5 h-5 text-orange-600" data-id="element-1649" />
                        <div data-id="element-1650">
                          <h4 className="font-semibold text-gray-900" data-id="element-1651">
                            {payment.billNumber}
                          </h4>
                          <p className="text-sm text-gray-600" data-id="element-1652">
                            Patient: {payment.patientName}
                          </p>
                        </div>
                      </div>
                      <span className="px-3 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800" data-id="element-1653">
                        {payment.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3" data-id="element-1654">
                      <div data-id="element-1655">
                        <p className="text-gray-600" data-id="element-1656">Original Amount</p>
                        <p className="font-medium text-gray-900" data-id="element-1657">
                          ₹{payment.originalAmount.toLocaleString()}
                        </p>
                      </div>
                      <div data-id="element-1658">
                        <p className="text-gray-600" data-id="element-1659">Paid Amount</p>
                        <p className="font-medium text-green-600" data-id="element-1660">
                          ₹{payment.paidAmount.toLocaleString()}
                        </p>
                      </div>
                      <div data-id="element-1661">
                        <p className="text-gray-600" data-id="element-1662">Remaining</p>
                        <p className="font-medium text-red-600" data-id="element-1663">
                          ₹{payment.remainingAmount.toLocaleString()}
                        </p>
                      </div>
                      <div data-id="element-1664">
                        <p className="text-gray-600" data-id="element-1665">Due Date</p>
                        <p className="font-medium text-gray-900" data-id="element-1666">
                          {payment.dueDate}
                        </p>
                      </div>
                    </div>
                    <div className="mb-3" data-id="element-1667">
                      <div className="flex justify-between text-sm mb-1" data-id="element-1668">
                        <span className="text-gray-600" data-id="element-1669">Payment Progress</span>
                        <span className="font-medium" data-id="element-1670">
                          {Math.round(payment.paidAmount / payment.originalAmount * 100)}
                          %
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1671">
                        <div className="bg-green-600 h-2 rounded-full" style={{
                    width: `${payment.paidAmount / payment.originalAmount * 100}%`
                  }} data-id="element-1672" />
                      </div>
                    </div>
                    <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm" data-id="element-1673">
                      Record Payment
                    </button>
                  </div>)}
              </div>
            </div>
          </>}
        {/* Invoices & Tax Tab */}
        {activeTab === 'invoices' && <>
            {/* Tax Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-1674">
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-1675">
                <div className="flex items-center space-x-2 mb-2" data-id="element-1676">
                  <FileTextIcon className="w-5 h-5 text-blue-600" data-id="element-1677" />
                  <span className="text-sm text-gray-600" data-id="element-1678">Total Invoices</span>
                </div>
                <p className="text-2xl font-bold text-gray-900" data-id="element-1679">156</p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-1680">This Month</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-1681">
                <div className="flex items-center space-x-2 mb-2" data-id="element-1682">
                  <DollarSignIcon className="w-5 h-5 text-green-600" data-id="element-1683" />
                  <span className="text-sm text-gray-600" data-id="element-1684">Total Tax Paid</span>
                </div>
                <p className="text-2xl font-bold text-gray-900" data-id="element-1685">₹224,100</p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-1686">GST 18%</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-1687">
                <div className="flex items-center space-x-2 mb-2" data-id="element-1688">
                  <CheckCircleIcon className="w-5 h-5 text-purple-600" data-id="element-1689" />
                  <span className="text-sm text-gray-600" data-id="element-1690">
                    Compliance Status
                  </span>
                </div>
                <p className="text-2xl font-bold text-green-600" data-id="element-1691">Active</p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-1692">All taxes filed</p>
              </div>
            </div>
            {/* Invoice Management */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6" data-id="element-1693">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1694">
                Invoice Management
              </h3>
              <div className="overflow-x-auto" data-id="element-1695">
                <table className="w-full" data-id="element-1696">
                  <thead className="bg-gray-50 border-b border-gray-200" data-id="element-1697">
                    <tr data-id="element-1698">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1699">
                        Invoice Number
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1700">
                        Patient
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1701">
                        Date
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-1702">
                        Subtotal
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-1703">
                        Tax (GST)
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" data-id="element-1704">
                        Total
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" data-id="element-1705">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200" data-id="element-1706">
                    {[...paymentHistory, ...pendingBills.map(b => ({
                  id: Math.random(),
                  billNumber: b.billNumber,
                  patientName: b.patientName,
                  date: b.date,
                  amount: b.total,
                  paymentMode: '-',
                  status: b.status
                }))].slice(0, 10).map(bill => {
                  const subtotal = Math.round(bill.amount / 1.18);
                  const tax = bill.amount - subtotal;
                  return <tr key={bill.id} className="hover:bg-gray-50" data-id="element-1707">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-1708">
                              {bill.billNumber}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1709">
                              {bill.patientName}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700" data-id="element-1710">
                              {bill.date}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-gray-900" data-id="element-1711">
                              ₹{subtotal.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-right text-gray-600" data-id="element-1712">
                              ₹{tax.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-medium text-gray-900" data-id="element-1713">
                              ₹{bill.amount.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-center" data-id="element-1714">
                              <div className="flex items-center justify-center space-x-2" data-id="element-1715">
                                <button className="text-blue-600 hover:text-blue-700 text-sm" data-id="element-1716">
                                  <DownloadIcon className="w-4 h-4" data-id="element-1717" />
                                </button>
                                <button className="text-gray-600 hover:text-gray-700 text-sm" data-id="element-1718">
                                  View
                                </button>
                              </div>
                            </td>
                          </tr>;
                })}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Tax Compliance */}
            <div className="bg-white border border-gray-200 rounded-lg p-6" data-id="element-1719">
              <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1720">
                Tax & Regulatory Compliance
              </h3>
              <div className="space-y-4" data-id="element-1721">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-id="element-1722">
                  <div className="flex items-start space-x-3" data-id="element-1723">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" data-id="element-1724" />
                    <div className="flex-1" data-id="element-1725">
                      <h4 className="font-medium text-green-900 mb-1" data-id="element-1726">
                        GST Compliance Active
                      </h4>
                      <p className="text-sm text-green-800" data-id="element-1727">
                        All invoices are GST compliant with 18% tax rate applied
                      </p>
                      <div className="mt-2 text-xs text-green-700" data-id="element-1728">
                        <p data-id="element-1729">GSTIN: 29ABCDE1234F1Z5</p>
                        <p data-id="element-1730">Last Filed: 2024-01-10</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1731">
                  <div className="border border-gray-200 rounded-lg p-4" data-id="element-1732">
                    <h4 className="font-medium text-gray-900 mb-2" data-id="element-1733">
                      Invoice Format
                    </h4>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-1734">
                      Standard GST compliant format with all required fields
                    </p>
                    <button className="text-blue-600 hover:text-blue-700 text-sm font-medium" data-id="element-1735">
                      View Sample Invoice
                    </button>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4" data-id="element-1736">
                    <h4 className="font-medium text-gray-900 mb-2" data-id="element-1737">
                      Tax Reports
                    </h4>
                    <p className="text-sm text-gray-600 mb-3" data-id="element-1738">
                      Download monthly and annual tax reports
                    </p>
                    <button className="text-blue-600 hover:text-blue-700 text-sm font-medium" data-id="element-1739">
                      <DownloadIcon className="w-4 h-4 inline mr-1" data-id="element-1740" />
                      Download Reports
                    </button>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4" data-id="element-1741">
                  <h4 className="font-medium text-gray-900 mb-3" data-id="element-1742">
                    Regulatory Documents
                  </h4>
                  <div className="space-y-2" data-id="element-1743">
                    <div className="flex items-center justify-between p-2 hover:bg-gray-50 rounded" data-id="element-1744">
                      <span className="text-sm text-gray-700" data-id="element-1745">
                        GST Registration Certificate
                      </span>
                      <button className="text-blue-600 hover:text-blue-700 text-sm" data-id="element-1746">
                        <DownloadIcon className="w-4 h-4 inline" data-id="element-1747" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between p-2 hover:bg-gray-50 rounded" data-id="element-1748">
                      <span className="text-sm text-gray-700" data-id="element-1749">
                        Annual Tax Returns (2023)
                      </span>
                      <button className="text-blue-600 hover:text-blue-700 text-sm" data-id="element-1750">
                        <DownloadIcon className="w-4 h-4 inline" data-id="element-1751" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between p-2 hover:bg-gray-50 rounded" data-id="element-1752">
                      <span className="text-sm text-gray-700" data-id="element-1753">
                        Monthly GST Returns
                      </span>
                      <button className="text-blue-600 hover:text-blue-700 text-sm" data-id="element-1754">
                        <DownloadIcon className="w-4 h-4 inline" data-id="element-1755" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>}
      </div>
      {/* Payment Modal */}
      {showPaymentModal && selectedBill && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1756">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-1757">
            <div className="p-6 border-b border-gray-200" data-id="element-1758">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-1759">
                Complete Payment
              </h2>
              <p className="text-gray-600 mt-1" data-id="element-1760">Bill: {selectedBill}</p>
            </div>
            <div className="p-6 space-y-6" data-id="element-1761">
              {/* Amount Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1762">
                <div className="flex items-center justify-between" data-id="element-1763">
                  <span className="text-gray-700" data-id="element-1764">Total Amount</span>
                  <span className="text-2xl font-bold text-blue-600" data-id="element-1765">
                    ₹
                    {pendingBills.find(b => b.billNumber === selectedBill)?.total.toLocaleString()}
                  </span>
                </div>
              </div>
              {/* Payment Mode Selection */}
              <div data-id="element-1766">
                <label className="block text-sm font-medium text-gray-700 mb-3" data-id="element-1767">
                  Select Payment Mode
                </label>
                <div className="grid grid-cols-2 gap-3" data-id="element-1768">
                  {paymentModes.map(mode => {
                const Icon = mode.icon;
                return <button key={mode.id} onClick={() => setSelectedPaymentMode(mode.id)} className={`p-4 border-2 rounded-lg transition-all ${selectedPaymentMode === mode.id ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1769">
                        <Icon className="w-8 h-8 text-blue-600 mx-auto mb-2" data-id="element-1770" />
                        <p className="text-sm font-medium text-gray-900" data-id="element-1771">
                          {mode.name}
                        </p>
                      </button>;
              })}
                </div>
              </div>
              {/* Payment Details Form */}
              {selectedPaymentMode && selectedPaymentMode !== 'invoice' && <div className="space-y-4" data-id="element-1772">
                  <h3 className="font-medium text-gray-900" data-id="element-1773">Payment Details</h3>
                  {selectedPaymentMode === 'card' && <>
                      <input type="text" placeholder="Card Number" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-1774" />
                      <div className="grid grid-cols-2 gap-4" data-id="element-1775">
                        <input type="text" placeholder="MM/YY" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-1776" />
                        <input type="text" placeholder="CVV" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-1777" />
                      </div>
                    </>}
                  {selectedPaymentMode === 'upi' && <input type="text" placeholder="UPI ID (e.g., name@upi)" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-1778" />}
                  {selectedPaymentMode === 'bank' && <>
                      <input type="text" placeholder="Account Number" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-1779" />
                      <input type="text" placeholder="IFSC Code" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-1780" />
                    </>}
                </div>}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3" data-id="element-1781">
              <button onClick={() => {
            setShowPaymentModal(false);
            setSelectedBill(null);
            setSelectedPaymentMode('');
          }} className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50" data-id="element-1782">
                Cancel
              </button>
              <button onClick={handleProcessPayment} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold" data-id="element-1783">
                {selectedPaymentMode === 'invoice' ? 'Generate Invoice' : `Pay ₹${pendingBills.find(b => b.billNumber === selectedBill)?.total.toLocaleString()}`}
              </button>
            </div>
          </div>
        </div>}
    </div>;
}